import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { apiPut } from "../../services/genesisApi/httpsMethods";
import { useReferenceLists } from "../../context/GlobalGetLists";
import type { ApiResponse } from "../../services/genesisApi/httpsMethods";
import { CustomButton } from "../../components/common/customButton";
import { ErrorDialog } from "../../components/common/errorDialog";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { areRecordsEqual, getChangedRecords } from "../../utils/compareData";
import { CustomTextInput } from "../../components/common/inputFields/customTextInput";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import { CustomIconButton } from "../../components/common/customIconButton";
import type { Sponsor } from "../../types/deal";
import { SPONSOR_TYPE_OPTIONS } from "../../constants/sponsorTypes";
import { MUISelectCellEditor } from "../../components/common/gridSelectCell";
import { HyperLinkRenderer } from "../../utils/hyperlinkRenderer";
import pencilIcon from "@/assets/svg/edit.svg";
import downloadIcon from "@/assets/svg/downloads-dark.svg";
import "../../assets/css/Text-General.css";
import { useCurrentUser } from "@/context/CurrentUserContext";
import "../../assets/css/AGGrid-custom.css";
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

const Sponsors = () => {
  const { canEdit } = useCurrentUser();
  const gridRef = useRef<AgGridReact>(null);
  const [editMode, setEditMode] = useState(false);
  const [filter, setFilter] = useState("");
  const [dirtyGrid, setDirtyGrid] = useState(false);
  const [newSponsors, setNewSponsors] = useState<Sponsor[]>([]);
  const [initialSponsors, setInitialSponsors] = useState<Sponsor[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  const { sponsors } = useReferenceLists();

  useEffect(() => {
    if (!sponsors) return;
    // Clone so grid edits never mutate the shared reference list.
    setNewSponsors(structuredClone(sponsors));
    setInitialSponsors(structuredClone(sponsors));
  }, [sponsors]);

  // Track if grid is dirty
  useEffect(() => {
    const isEqual = areRecordsEqual(initialSponsors, newSponsors);
    setDirtyGrid(!isEqual);
  }, [initialSponsors, newSponsors]);

  const onCellValueChanged = useCallback((event: any) => {
    const { colDef, newValue, oldValue, data } = event;
    if (newValue === oldValue) return;
    const field = colDef.field;

    // Store the row's own value (what the column's valueSetter wrote), not
    // newValue, which comes from the valueGetter and may be a display shape.
    setNewSponsors((prevSponsor: Sponsor[]) =>
      prevSponsor.map((sponsor) =>
        sponsor.id === data.id ? { ...sponsor, [field]: data[field] } : sponsor,
      ),
    );
  }, []);

  const handleCancelButtonPress = () => {
    setEditMode(false);
    // Clone so AG Grid edits don't mutate the baseline rows.
    setNewSponsors(structuredClone(initialSponsors)); // Reset to original
    setDirtyGrid(false);
  };

  const handleSaveButtonPress = async () => {
    if (isSaving) return;
    const changedItems = getChangedRecords(newSponsors, initialSponsors);
    setIsSaving(true);
    const updates = changedItems.map((sponsor) => {
      return apiPut("sponsor", sponsor.id, sponsor).catch(
        (error): ApiResponse => {
          console.error(`Failed to update sponsor ${sponsor.id}`, error);
          return {
            success: false,
            status: 0,
            message: "Failed to save sponsor changes. Please try again.",
          };
        },
      );
    });
    const results = await Promise.all(updates);
    const failedResponse = results.find((r) => !r?.success);
    if (failedResponse) {
      // Stay in edit mode and keep the user's edits so they can retry.
      setIsSaving(false);
      showErrorFromResponse(failedResponse);
      return;
    }
    window.location.reload();
  };

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSaveButtonPress);

  const filteredRows = useMemo(() => {
    return newSponsors.filter((s) =>
      s.sponsor_name?.toLowerCase().includes(filter.toLowerCase()),
    );
  }, [newSponsors, filter]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: "sponsor_name",
        headerName: "Sponsor",
        editable: editMode,
        width: 350,
        cellRenderer: HyperLinkRenderer("sponsors"),
      },
      {
        field: "sponsor_type",
        headerName: "Type",
        editable: editMode,
        width: 240,
        cellEditor: MUISelectCellEditor,
        cellEditorParams: {
          values: [
            { id: null, label: "(None)" },
            ...SPONSOR_TYPE_OPTIONS.map((t) => ({ id: t, label: t })),
          ],
          label: "Type",
          multiple: false,
          squareSelectCorners: true,
        },
        // Plain string (or null) so empty cells render blank; the editor maps it
        // to an option and "(None)" stays available in the dropdown to clear it.
        valueGetter: (params) => {
          const v = params.data?.sponsor_type;
          return v == null || v === "" ? null : String(v);
        },
        valueSetter: (params) => {
          if (!params.data) return false;
          const nv = params.newValue;
          // Use the option id only: the "(None)" option has id null, and falling
          // back to its label would save the literal string "(None)".
          const id = nv == null ? null : typeof nv === "object" ? nv.id : nv;
          params.data.sponsor_type =
            id == null || id === "" ? null : String(id);
          return true;
        },
        valueFormatter: ({ value }) => {
          if (value == null) return "";
          if (typeof value === "object") return value.label || value.id || "";
          return String(value);
        },
      },
      {
        field: "short_description",
        headerName: "Short Description",
        editable: editMode,
        width: 300,
      },
      {
        field: "credit_assessment",
        headerName: "Credit Assessment",
        editable: editMode,
        width: 300,
      },
      {
        field: "credit_risk",
        headerName: "Credit Risk",
        editable: editMode,
        width: 120,
      },
      { field: "deal_count", headerName: "Deals", editable: false, width: 90 },
      {
        field: "live_deal_count",
        headerName: "Live Deals",
        editable: false,
        width: 110,
      },
      {
        field: "issued_loan_count",
        headerName: "Issued Loans",
        editable: false,
        width: 120,
      },
    ],
    [editMode],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        padding: 0,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <SimpleToolbar>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
              minWidth: 0,
            }}
          >
            <CustomTextInput
              label="Sponsor name"
              value={filter}
              onChange={(val) => typeof val === "string" && setFilter(val)}
            />
          </Box>
          {editMode ? (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: "15px" }}
              data-position="right"
            >
              <CustomButton
                text="Cancel"
                handleButtonPress={handleCancelButtonPress}
                isButtonDisabled={isSaving}
                solid={false}
                widthOverride="100px"
              />
              <CustomButton
                text="Save"
                handleButtonPress={requestSave}
                isButtonDisabled={(!dirtyGrid && !isCellEditing) || isSaving}
                isLoading={isSaving}
                isLoadingText="Saving"
                solid
                widthOverride="100px"
              />
            </Box>
          ) : (
            <Box
              data-position="right"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                ml: -0.5,
                "& .MuiIconButton-root": {
                  p: 0.6,
                },
                "& .MuiIconButton-root .MuiSvgIcon-root": {
                  fontSize: 22,
                },
              }}
            >
              <CustomIconButton
                tooltip="Download"
                icon={
                  <Box
                    component="img"
                    src={downloadIcon}
                    alt="Download sponsors"
                    sx={{ width: 30, height: 30, display: "block" }}
                  />
                }
                iconButtonSx={{ p: 0 }}
                onClick={() =>
                  gridRef.current?.api.exportDataAsCsv({ fileName: "sponsors" })
                }
              />
              <CustomIconButton
                tooltip="Edit"
                icon={
                  <Box
                    component="img"
                    src={pencilIcon}
                    alt="Edit sponsors"
                    sx={{ width: 20, height: 20, display: "block" }}
                  />
                }
                disabled={!canEdit}
                onClick={() => {
                  if (!canEdit) return;
                  setEditMode(true);
                }}
              />
            </Box>
          )}
        </SimpleToolbar>
      </Box>

      {/* AG Grid */}
      <Box sx={{ flexGrow: 1, minHeight: 0, p: 0 }}>
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact
            {...gridEditProps}
            ref={gridRef}
            rowData={filteredRows}
            columnDefs={columnDefs}
            onCellValueChanged={onCellValueChanged}
            singleClickEdit
            defaultColDef={{ resizable: true }}
            rowHeight={30}
            getRowId={({ data }) => data?.id?.toString()}
          />
        </div>
      </Box>
      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

export default Sponsors;
